/**
 * Email Account Setup - Connect IMAP/Google accounts
 *
 * Wizard-style setup for:
 * - IMAP accounts with manual configuration
 * - Google OAuth (future)
 * - Microsoft OAuth (future)
 */

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import emailApi, { CreateAccountRequest, EmailAccount, SyncStatus } from '@/services/emailApi';

interface EmailAccountSetupProps {
  onComplete: (account: EmailAccount) => void;
  onCancel: () => void;
}

type Provider = 'imap' | 'google';
type Step = 'provider' | 'credentials' | 'testing' | 'syncing' | 'complete';

interface FormData {
  provider: Provider;
  email: string;
  display_name: string;
  // IMAP settings
  imap_host: string;
  imap_port: number;
  imap_username: string;
  imap_password: string;
  // SMTP settings
  smtp_host: string;
  smtp_port: number;
  smtp_username: string;
  smtp_password: string;
  use_ssl: boolean;
}

const COMMON_PROVIDERS: {
  name: string;
  domain: string;
  imap_host: string;
  imap_port: number;
  smtp_host: string;
  smtp_port: number;
}[] = [
  { name: 'Gmail', domain: 'gmail.com', imap_host: 'imap.gmail.com', imap_port: 993, smtp_host: 'smtp.gmail.com', smtp_port: 587 },
  { name: 'Outlook/Hotmail', domain: 'outlook.com', imap_host: 'outlook.office365.com', imap_port: 993, smtp_host: 'smtp.office365.com', smtp_port: 587 },
  { name: 'Yahoo', domain: 'yahoo.com', imap_host: 'imap.mail.yahoo.com', imap_port: 993, smtp_host: 'smtp.mail.yahoo.com', smtp_port: 587 },
  { name: 'iCloud', domain: 'icloud.com', imap_host: 'imap.mail.me.com', imap_port: 993, smtp_host: 'smtp.mail.me.com', smtp_port: 587 },
];

const EmailAccountSetup: React.FC<EmailAccountSetupProps> = ({ onComplete, onCancel }) => {
  const { t } = useTranslation('email');
  const [step, setStep] = useState<Step>('provider');
  const [error, setError] = useState<string | null>(null);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [createdAccount, setCreatedAccount] = useState<EmailAccount | null>(null);
  const [syncStatus, setSyncStatus] = useState<SyncStatus | null>(null);

  const [formData, setFormData] = useState<FormData>({
    provider: 'imap',
    email: '',
    display_name: '',
    imap_host: '',
    imap_port: 993,
    imap_username: '',
    imap_password: '',
    smtp_host: '',
    smtp_port: 587,
    smtp_username: '',
    smtp_password: '',
    use_ssl: true,
  });

  // Auto-detect provider settings from email
  const detectProvider = (email: string) => {
    const domain = email.split('@')[1]?.toLowerCase();
    if (!domain) return;

    const provider = COMMON_PROVIDERS.find(p =>
      domain.includes(p.domain) || p.domain.includes(domain)
    );

    if (provider) {
      setFormData(prev => ({
        ...prev,
        imap_host: provider.imap_host,
        imap_port: provider.imap_port,
        smtp_host: provider.smtp_host,
        smtp_port: provider.smtp_port,
        imap_username: email,
        smtp_username: email,
      }));
    }
  };

  const handleEmailChange = (email: string) => {
    setFormData(prev => ({ ...prev, email, imap_username: email, smtp_username: email }));
    detectProvider(email);
  };

  const startSyncPolling = (accountId: string, account: EmailAccount) => {
    // Poll every 3 seconds for up to 2 minutes
    const maxPolls = 40;
    let polls = 0;

    const poll = async () => {
      try {
        const status = await emailApi.sync.getStatus(accountId);
        setSyncStatus(status);

        // 'idle' means sync completed, 'error' means sync failed
        if (status.status === 'idle' || status.status === 'error') {
          setStep('complete');
          setTimeout(() => onComplete(account), 2000);
          return;
        }

        polls++;
        if (polls < maxPolls) {
          setTimeout(poll, 3000);
        } else {
          // Timeout - sync still running, let them proceed
          setStep('complete');
          setTimeout(() => onComplete(account), 2000);
        }
      } catch {
        // Error polling - still let them proceed
        setStep('complete');
        setTimeout(() => onComplete(account), 2000);
      }
    };

    poll();
  };

  const handleSubmit = async () => {
    setError(null);
    setStep('testing');

    try {
      // Step 0: Check if account already exists
      const existingAccounts = await emailApi.accounts.list();
      const duplicate = existingAccounts.find(
        (acc) => acc.email.toLowerCase() === formData.email.toLowerCase()
      );
      if (duplicate) {
        setError(`Account ${formData.email} already exists. Delete it first if you want to re-add.`);
        setStep('credentials');
        return;
      }

      // Step 1: Test credentials
      const testResult = await emailApi.accounts.testCredentials({
        email: formData.email,
        imap_host: formData.imap_host,
        imap_port: formData.imap_port,
        imap_username: formData.imap_username || formData.email,
        imap_password: formData.imap_password,
        use_ssl: formData.use_ssl,
      });

      if (!testResult.success) {
        setError(testResult.message || 'Connection failed - check credentials');
        setStep('credentials');
        return;
      }

      // Step 2: Only create account if test passed
      const request: CreateAccountRequest = {
        email: formData.email,
        display_name: formData.display_name || formData.email,
        provider: formData.provider,
        imap_host: formData.imap_host,
        imap_port: formData.imap_port,
        imap_username: formData.imap_username || formData.email,
        imap_password: formData.imap_password,
        smtp_host: formData.smtp_host,
        smtp_port: formData.smtp_port,
        smtp_username: formData.smtp_username || formData.email,
        smtp_password: formData.smtp_password || formData.imap_password,
        use_ssl: formData.use_ssl,
      };

      const account = await emailApi.accounts.create(request);
      setCreatedAccount(account);
      setStep('syncing');
      startSyncPolling(account.id, account);
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message :
        typeof err === 'object' && err !== null && 'response' in err
          ? (err as { response?: { data?: { detail?: string } } }).response?.data?.detail || 'Connection failed'
          : 'Connection failed';
      setError(errorMessage);
      setStep('credentials');
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-4 text-white">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold">{t('account_setup.connect_email_account')}</h2>
            <button
              onClick={onCancel}
              className="p-1 hover:bg-white/20 rounded-lg transition"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          {/* Progress */}
          <div className="flex items-center gap-2 mt-4">
            {['Provider', 'Credentials', 'Testing', 'Syncing', 'Complete'].map((label, idx) => {
              const stepIdx = ['provider', 'credentials', 'testing', 'syncing', 'complete'].indexOf(step);
              const isActive = idx === stepIdx;
              const isComplete = idx < stepIdx;
              return (
                <React.Fragment key={label}>
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                    isComplete ? 'bg-green-500 text-white' :
                    isActive ? 'bg-white text-blue-600' :
                    'bg-blue-500/30 text-white/70'
                  }`}>
                    {isComplete ? '✓' : idx + 1}
                  </div>
                  {idx < 4 && (
                    <div className={`flex-1 h-1 rounded ${isComplete ? 'bg-green-500' : 'bg-blue-500/30'}`} />
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Step 1: Provider Selection */}
          {step === 'provider' && (
            <div className="space-y-4">
              <p className="text-gray-600 text-sm">{t('account_setup.choose_connection_method')}</p>

              <div className="grid gap-3">
                <button
                  onClick={() => {
                    setFormData(prev => ({ ...prev, provider: 'imap' }));
                    setStep('credentials');
                  }}
                  className="flex items-center gap-4 p-4 border-2 border-gray-200 rounded-xl hover:border-blue-500 hover:bg-blue-50 transition text-left"
                >
                  <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                    <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <div>
                    <div className="font-semibold text-gray-900">{t('account_setup.imap_smtp')}</div>
                    <div className="text-sm text-gray-500">{t('account_setup.imap_description')}</div>
                  </div>
                </button>

                <button
                  disabled
                  className="flex items-center gap-4 p-4 border-2 border-gray-100 rounded-xl opacity-50 cursor-not-allowed text-left"
                >
                  <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center">
                    <svg className="w-6 h-6 text-gray-400" viewBox="0 0 24 24">
                      <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                      <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                      <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                      <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                    </svg>
                  </div>
                  <div>
                    <div className="font-semibold text-gray-900">{t('account_setup.google')}</div>
                    <div className="text-sm text-gray-500">{t('account_setup.coming_soon_oauth')}</div>
                  </div>
                </button>

                <button
                  disabled
                  className="flex items-center gap-4 p-4 border-2 border-gray-100 rounded-xl opacity-50 cursor-not-allowed text-left"
                >
                  <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center">
                    <svg className="w-6 h-6 text-gray-400" viewBox="0 0 24 24">
                      <path fill="currentColor" d="M21.17 2.06H2.83c-.65 0-1.17.52-1.17 1.17v17.54c0 .65.52 1.17 1.17 1.17h18.34c.65 0 1.17-.52 1.17-1.17V3.23c0-.65-.52-1.17-1.17-1.17zm-1.76 2.34L12 9.69 4.59 4.4h14.82zM4.06 19.06V6.41l7.94 5.4 7.94-5.4v12.65H4.06z"/>
                    </svg>
                  </div>
                  <div>
                    <div className="font-semibold text-gray-900">{t('account_setup.microsoft_365')}</div>
                    <div className="text-sm text-gray-500">{t('account_setup.coming_soon_oauth')}</div>
                  </div>
                </button>
              </div>
            </div>
          )}

          {/* Step 2: Credentials */}
          {step === 'credentials' && (
            <div className="space-y-4">
              {error && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                  {error}
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('account_setup.email_address')} *</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={e => handleEmailChange(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="you@example.com"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('account_setup.password')} *</label>
                <input
                  type="password"
                  value={formData.imap_password}
                  onChange={e => setFormData(prev => ({ ...prev, imap_password: e.target.value }))}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="••••••••"
                  required
                />
                <p className="mt-1 text-xs text-gray-500">
                  For Gmail/Outlook, use an App Password. <a href="https://support.google.com/accounts/answer/185833" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">Learn more</a>
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('account_setup.display_name')}</label>
                <input
                  type="text"
                  value={formData.display_name}
                  onChange={e => setFormData(prev => ({ ...prev, display_name: e.target.value }))}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder={t('account_setup.display_name')}
                />
              </div>

              {/* Advanced Settings */}
              <div>
                <button
                  type="button"
                  onClick={() => setShowAdvanced(!showAdvanced)}
                  className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900"
                >
                  <svg className={`w-4 h-4 transition-transform ${showAdvanced ? 'rotate-90' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                  {t('account_setup.advanced_settings')}
                </button>

                {showAdvanced && (
                  <div className="mt-3 p-4 bg-gray-50 rounded-lg space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">IMAP Host</label>
                        <input
                          type="text"
                          value={formData.imap_host}
                          onChange={e => setFormData(prev => ({ ...prev, imap_host: e.target.value }))}
                          className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                          placeholder="imap.example.com"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">IMAP Port</label>
                        <input
                          type="number"
                          value={formData.imap_port}
                          onChange={e => setFormData(prev => ({ ...prev, imap_port: parseInt(e.target.value) || 993 }))}
                          className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">SMTP Host</label>
                        <input
                          type="text"
                          value={formData.smtp_host}
                          onChange={e => setFormData(prev => ({ ...prev, smtp_host: e.target.value }))}
                          className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                          placeholder="smtp.example.com"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">SMTP Port</label>
                        <input
                          type="number"
                          value={formData.smtp_port}
                          onChange={e => setFormData(prev => ({ ...prev, smtp_port: parseInt(e.target.value) || 587 }))}
                          className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                    </div>

                    <label className="flex items-center gap-2 text-sm text-gray-700">
                      <input
                        type="checkbox"
                        checked={formData.use_ssl}
                        onChange={e => setFormData(prev => ({ ...prev, use_ssl: e.target.checked }))}
                        className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      {t('account_setup.use_ssl')}
                    </label>
                  </div>
                )}
              </div>

              {/* Actions */}
              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setStep('provider')}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition"
                >
                  {t('common:buttons.back')}
                </button>
                <button
                  type="button"
                  onClick={handleSubmit}
                  disabled={!formData.email || !formData.imap_password || !formData.imap_host}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {t('account_setup.connect')}
                </button>
              </div>
            </div>
          )}

          {/* Step 3: Testing */}
          {step === 'testing' && (
            <div className="text-center py-8">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-blue-100 mb-4">
                <svg className="w-8 h-8 text-blue-600 animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">{t('account_setup.connecting')}</h3>
              <p className="text-sm text-gray-500">{t('account_setup.testing_connection')}</p>
            </div>
          )}

          {/* Step 4: Syncing */}
          {step === 'syncing' && (
            <div className="text-center py-8">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-blue-100 mb-4">
                <svg className="w-8 h-8 text-blue-600 animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">{t('account_setup.sync_in_progress')}</h3>
              <p className="text-sm text-gray-500 mb-4">
                {t('account_setup.downloading_emails')}
              </p>
              {syncStatus?.progress && (
                <div className="mb-4">
                  <div className="text-2xl font-bold text-blue-600 mb-1">
                    {syncStatus.progress.current} / {syncStatus.progress.total}
                  </div>
                  <div className="text-xs text-gray-500">
                    Syncing: {syncStatus.progress.folder}
                  </div>
                </div>
              )}
              {syncStatus?.status === 'error' && syncStatus.error_message && (
                <div className="text-sm text-red-600 mb-4">
                  Error: {syncStatus.error_message}
                </div>
              )}
              <p className="text-xs text-gray-400 mb-6">
                {t('account_setup.sync_background_notice')}
              </p>
              <button
                onClick={() => {
                  setStep('complete');
                  if (createdAccount) onComplete(createdAccount);
                }}
                className="px-4 py-2 text-sm text-gray-600 hover:text-gray-900"
              >
                {t('account_setup.continue_background')}
              </button>
            </div>
          )}

          {/* Step 5: Complete */}
          {step === 'complete' && (
            <div className="text-center py-8">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-100 mb-4">
                <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">{t('account_setup.account_connected')}</h3>
              <p className="text-sm text-gray-500">{t('account_setup.sync_may_take_time')}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EmailAccountSetup;
